import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import CommonLayout from "../../../layouts/CommonLayout";
import { allAPiServicesCall } from "../../../services/apiServices";
import { toast } from "react-toastify";
import { getAuthConfig } from "../../../services/apiUtils";
import ConfirmationModal from "../ConfirmationModal/ConfirmationModal";
import SetTopAlbumPriorityModel from "../../../components/Model/SetTopAlbumPriorityModel";
import { albumImageUrl, extractObjectId, albumArtistName, formatViewCount } from "../../../utils/utils";
import MultiGenreSelect from "../../../components/common/MultiGenreSelect";
import PageTargetSelector from "../../../components/common/PageTargetSelector";
import usePageTargetSelection from "../../../hooks/usePageTargetSelection";
import {
    FaCompactDisc,
    FaSearch,
    FaTimes,
    FaMusic,
    FaEye,
    FaStar,
    FaTrash,
    FaPlus,
    FaLayerGroup
} from "react-icons/fa";
import "./TopExplore.css";

const TopExplore = () => {
    const navigate = useNavigate();
    const pageTarget = usePageTargetSelection();
    const targetParams = pageTarget.selectedTargetParams;
    const [albums, setAlbums] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [albumToDelete, setAlbumToDelete] = useState(null);
    const [showTopAlbumPrioritEditModal, setTopAlbumPrioritShowEditModal] = useState(false);
    const [topAlbumEditData, setTopAlbumDetailEditData] = useState({});
    const [genres, setGenres] = useState([]);
    const [selectedGenresByAlbum, setSelectedGenresByAlbum] = useState({});

    const fetchGenres = async () => {
        try {
            const response = await allAPiServicesCall.getGenres(
                { page: 1, limit: 1000 },
                getAuthConfig()
            );
            if (response?.success && Array.isArray(response.data)) {
                setGenres(response.data);
            }
        } catch (error) {
            console.error("Error fetching genres:", error);
        }
    };

    useEffect(() => {
        fetchGenres();
    }, []);

    const fetchTopExploreAlbums = async ($page, $limit) => {
        setLoading(true);
        try {
            const response = await allAPiServicesCall.getTopExploreAlbums({ $page, $limit, ...targetParams }, getAuthConfig());
            if (response && response?.success) {
                setAlbums(response?.data?.albums);
            } else {
                toast.warn("No data found or invalid response.");
            }
        } catch (error) {
            console.error("Error fetching top explore albums:", error);
            toast.error("Failed to fetch top explore albums.");
        } finally {
            setLoading(false);
        }
    };

    const fetchSearchResults = async (album_name) => {
        setLoading(true);
        try {
            let response = await allAPiServicesCall.getSearchAlbum(
                { album_name },
                getAuthConfig()
            );
            const searchList = response.data || [];
            if (searchList.length > 0) {
                setSearchResults(searchList);
            } else {
                setSearchResults([]);
                toast.info("No results found");
            }
        } catch (error) {
            console.error("Error fetching search artist:", error);
            toast.error("Failed to fetch search artists");
        } finally {
            setLoading(false);
        }
    };

    const addAlbumToTop = async (albumId) => {
        const genreArray = selectedGenresByAlbum[albumId] || [];
        const genre = genreArray.length > 0 ? genreArray.join(", ") : null;

        setLoading(true);
        try {
            const payload = { album_id: albumId, genre, ...targetParams };
            let response = await allAPiServicesCall.addTopAlbum(payload, getAuthConfig(), navigate);

            if (response && response.success) {
                setSearchTerm("");
                setSelectedGenresByAlbum({});
                fetchTopExploreAlbums(null, null);
            }
        } catch (error) {
            console.error("Error adding top album:", error);
            toast.error("Failed to add top album.");
        } finally {
            setLoading(false);
        }
    };

    const handleGenreSelect = (albumId, genreArray) => {
        setSelectedGenresByAlbum(prev => ({
            ...prev,
            [albumId]: genreArray
        }));
    };

    const handleOnDeleteClick = (albumId) => {
        setAlbumToDelete(albumId);
        setShowModal(true);
    };

    const handleConfirmDelete = async () => {
        setLoading(true);
        setShowModal(false);
        try {
            let payload = { id: albumToDelete };
            await allAPiServicesCall.deleteTopExploreAlbum(payload, getAuthConfig());
            toast.success("Album deleted successfully!");
            fetchTopExploreAlbums(null, null);
        } catch (error) {
            console.error("There was an error deleting the album!", error);
            toast.error("Failed to delete album.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!searchTerm.trim()) {
            fetchTopExploreAlbums(null, null);
        }
    }, [targetParams.pageType, targetParams.genreSlug]);

    useEffect(() => {
        if (searchTerm.trim()) {
            fetchSearchResults(searchTerm);
        } else {
            fetchTopExploreAlbums(null, null);
        }
    }, [searchTerm]);

    const setTopAlbumPriority = (ArtistId) => {
        setTopAlbumPrioritShowEditModal(true);
        setTopAlbumDetailEditData(ArtistId);
    };

    const clearSearch = () => {
        setSearchTerm("");
        setSearchResults([]);
    };

    const filteredAlbums = searchTerm.trim() ? searchResults : albums;

    const getAlbumGenres = (genreValue) => {
        if (!genreValue) return [];
        if (Array.isArray(genreValue)) {
            return genreValue.filter(Boolean);
        }
        if (typeof genreValue === "string") {
            return genreValue
                .split(",")
                .map((genre) => genre.trim())
                .filter(Boolean);
        }
        return [];
    };

    return (
        <CommonLayout>
            <div className="top-explore-page">
                {/* Header Section */}
                <div className="explore-header">
                    <div className="explore-header-content">
                        <div className="explore-header-icon">
                            <FaCompactDisc />
                        </div>
                        <div className="explore-header-text">
                            <h1 className="explore-title">Top Explore Albums</h1>
                            <p className="explore-subtitle">
                                Explore top albums and artists with their details
                            </p>
                        </div>
                    </div>
                    <div className="explore-stats">
                        <div className="explore-stat-card">
                            <FaCompactDisc className="explore-stat-icon" />
                            <div className="explore-stat-info">
                                <span className="explore-stat-value">{albums.length}</span>
                                <span className="explore-stat-label">Albums</span>
                            </div>
                        </div>
                        <div className="explore-stat-card">
                            <FaLayerGroup className="explore-stat-icon" />
                            <div className="explore-stat-info">
                                <span className="explore-stat-value">{genres.length}</span>
                                <span className="explore-stat-label">Genres</span>
                            </div>
                        </div>
                    </div>
                </div>

                <PageTargetSelector
                    pageTarget={pageTarget}
                    subtitle="Switch between Homepage and Genre targets before curating top explore albums."
                />

                {/* Search & Filter Section */}
                <div className="explore-controls">
                    <div className="explore-search-wrapper">
                        <FaSearch className="explore-search-icon" />
                        <input
                            type="text"
                            className="explore-search-input"
                            placeholder="Search album to add to top explore..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        {searchTerm && (
                            <button
                                className="explore-search-clear"
                                onClick={clearSearch}
                            >
                                <FaTimes />
                            </button>
                        )}
                    </div>
                </div>

                {/* Search Badge */}
                {searchTerm.trim() && (
                    <div className="explore-search-badge">
                        <FaSearch />
                        Searching for "{searchTerm}"
                    </div>
                )}

                {/* Content Section */}
                <div className="explore-content">
                    {loading ? (
                        <div className="explore-loading">
                            <div className="explore-loading-spinner">
                                <Spinner animation="border" />
                            </div>
                            <p className="explore-loading-text">Loading albums...</p>
                        </div>
                    ) : filteredAlbums.length > 0 ? (
                        <div className="explore-grid">
                            {filteredAlbums.map((album, index) => (
                                <div
                                    key={album._id}
                                    className={`explore-card ${searchTerm.trim() ? 'explore-card-search' : ''}`}
                                >
                                    <div className="explore-card-image-wrapper">
                                        <img
                                            src={albumImageUrl(album)}
                                            alt={album.album_name}
                                            className="explore-card-image"
                                            onClick={() => navigate(`/album/songs/${album.album_id}`)}
                                        />
                                        <div className="explore-card-overlay">
                                            <span className="explore-card-rank">
                                                <FaCompactDisc className="explore-card-rank-icon" />
                                                #{index + 1}
                                            </span>
                                            <span className={`explore-card-badge ${searchTerm.trim() ? 'explore-card-badge-search' : ''}`}>
                                                {searchTerm.trim() ? (
                                                    <><FaSearch /> Search</>
                                                ) : (
                                                    <><FaCompactDisc /> Explore</>
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="explore-card-content">
                                        <h3 className="explore-card-name">{album.album_name || "No Album Name"}</h3>
                                        <p className="explore-card-artist">
                                            <strong>Artist:</strong> {albumArtistName(album)}
                                        </p>
                                        {formatViewCount(album.popularity ?? album.spotifyListeners ?? album.totalPlays) && (
                                            <p className="explore-card-artist" style={{ fontSize: '0.85em', opacity: 0.8 }}>
                                                <strong>Plays:</strong> {formatViewCount(album.popularity ?? album.spotifyListeners ?? album.totalPlays)}
                                            </p>
                                        )}

                                        {/* Genre Tags */}
                                        {getAlbumGenres(album.genre).length > 0 && (
                                            <div className="explore-card-genres">
                                                {getAlbumGenres(album.genre).slice(0, 3).map((g, i) => (
                                                    <span key={i} className="explore-genre-tag">
                                                        {g.trim()}
                                                    </span>
                                                ))}
                                                {getAlbumGenres(album.genre).length > 3 && (
                                                    <span className="explore-genre-tag">
                                                        +{getAlbumGenres(album.genre).length - 3}
                                                    </span>
                                                )}
                                            </div>
                                        )}

                                        {/* Actions */}
                                        <div className="explore-card-actions">
                                            {searchTerm.trim() ? (
                                                <>
                                                    <div className="explore-genre-select">
                                                        <MultiGenreSelect
                                                            genres={genres}
                                                            selectedGenres={selectedGenresByAlbum[album.album_id] || []}
                                                            onChange={(genreArray) => handleGenreSelect(album.album_id, genreArray)}
                                                            placeholder="Select Genres (Optional)"
                                                            size="sm"
                                                        />
                                                    </div>
                                                    <button
                                                        className="explore-action-btn explore-action-btn-add"
                                                        onClick={() => addAlbumToTop(album.album_id)}
                                                    >
                                                        <FaPlus /> Add To Top Explore
                                                    </button>
                                                </>
                                            ) : (
                                                <div className="explore-card-actions-row">
                                                    <button
                                                        className="explore-action-btn explore-action-btn-view"
                                                        onClick={() => navigate(`/album/songs/${album.album_id}`)}
                                                    >
                                                        <FaEye /> View Songs
                                                    </button>
                                                    <button
                                                        className="explore-action-btn explore-action-btn-priority"
                                                        onClick={() => setTopAlbumPriority(album)}
                                                    >
                                                        <FaStar /> Priority
                                                    </button>
                                                    <button
                                                        className="explore-action-btn explore-action-btn-delete"
                                                        onClick={() => handleOnDeleteClick(extractObjectId(album._id))}
                                                    >
                                                        <FaTrash /> Delete
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="explore-empty">
                            <div className="explore-empty-icon">
                                <FaCompactDisc />
                            </div>
                            <h3 className="explore-empty-title">
                                {searchTerm.trim() ? "No results found" : "No albums found"}
                            </h3>
                            <p className="explore-empty-text">
                                {searchTerm.trim()
                                    ? "Try searching with different keywords to find albums"
                                    : "Search for albums above to add them to your top explore collection"
                                }
                            </p>
                            {searchTerm.trim() && (
                                <button
                                    className="explore-empty-btn"
                                    onClick={clearSearch}
                                >
                                    <FaTimes /> Clear Search
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Confirmation Modal */}
            <ConfirmationModal
                show={showModal}
                handleClose={() => setShowModal(false)}
                onConfirm={handleConfirmDelete}
                message="Are you sure you want to delete this album?"
            />

            {/* Priority Modal */}
            <SetTopAlbumPriorityModel
                show={showTopAlbumPrioritEditModal}
                handleClose={() => setTopAlbumPrioritShowEditModal(false)}
                albumDetails={topAlbumEditData}
                genres={genres}
                handleFormSubmit={() => fetchTopExploreAlbums(null, null)}
            />
        </CommonLayout>
    );
};

export default TopExplore;
